import React, {useState, useEffect, Fragment} from 'react'
import {Link, withRouter} from 'react-router-dom'
import AudioGuideEpisode from '../AudioGuideEpisode/AudioGuideEpisode'
import styles from './AudioGuideEpisodesList.css'
import { getPathFromAbsoluteUrl, localizeUrl, secondsToMinutes } from '../../../services/service-utils'
import { LIB } from '../../../constants/consts-types'
import { getPageData } from '../../../services/service-wp'
import DonutSpinner from '../../DonutSpinner/DonutSpinner'
import AudioGuideMapModal from '../AudioGuideMap/AudioGuideMapModal'
import { _try } from '../../../services/service-utils'

const fetchAndSetEpisodesData = async (language, audio_guide_list = [], setEpisodesAll) => {
  try {
    const episodesData = await Promise.all(audio_guide_list.map(async audioGuideList => {
      // const url = localizeUrl(language, `/wp-json/smk/v1/pages?_embed&path=/${audioGuideList.audio_guide_episode_item.post_type}/${audioGuideList.audio_guide_episode_item.post_name}/`)
      // return (await fetch(url)).json()
      const path = localizeUrl(language, `/${audioGuideList.audio_guide_episode_item.post_type}/${audioGuideList.audio_guide_episode_item.post_name}/`)
      return await getPageData(path, '?_embed')
    }))
    setEpisodesAll(episodesData || [])
  } catch (err) {
    console.log("Fetch error: ", err)
    setEpisodesAll([])
  }
}

const fetchEpisodesArtworkData = async (episodes = [], setEpisodesArtworkData) => {
  // Fetch a list of artworks from API to determine which ones are on display

  const artworksIds = episodes
    .map(episode => _try(() => episode.acf.artwork_id))
    .filter(episode => !!episode)
  const artworksIdsAsString = artworksIds.join().trim()
  const fetchArtworksUrl = `https://api.smk.dk/api/v1/art/?object_number=${artworksIdsAsString}&lang=da`

  try {
    const result = await fetch(fetchArtworksUrl)
    const jsonResult = await result.json()
    setEpisodesArtworkData((jsonResult && jsonResult.items) || [])
  } catch (err) {
    console.log("Fetch error: ", err)
    // NOTE: Bit curious use case. If the API fails, we should act as if items are on display.
    // Otherwise the episodes list would be empty
    const mockArtworkList = artworksIds.map(artworkId => ({ object_number: artworkId, on_display: true}))
    setEpisodesArtworkData(mockArtworkList)
  }
}

const episodesFilter = (episodesAll, episodesArtworkData) => (
  (episodesAll && episodesAll.length && episodesAll
    .filter(episode => {
      if (!episode || episode.status !== 'publish' || !episodesArtworkData || !episodesArtworkData.length) {
        return false
      }
      const { artwork_id } = episode.acf || {}
      const artworksIds = episodesArtworkData
        .filter(artwork => artwork.on_display)
        .map(artwork => artwork.object_number)

      return (artwork_id && artworksIds.includes(artwork_id)) || !artwork_id
    })) || []
)

const AudioGuideEpisodesList = ({ currentRoute }) => {
  const { title, colors, language, smk_guide: guide = {}, audio_guide_list = [], audio_guide_list_description } = currentRoute

  const [showMap, setShowMap] = useState(false)
  const [mapClosing, setMapClosing] = useState(false)
  const [mapArtwork, setMapArtwork] = useState(null)
  const [episodesAll, setEpisodesAll] = useState(null)
  const [episodesArtworkData, setEpisodesArtworkData] = useState(null)

  const episodesFiltered = episodesFilter(episodesAll, episodesArtworkData)

  const listGuideTotalDuration = (episodesFiltered && 
    episodesFiltered.reduce((acc, item) => acc + Number(_try(() => item.acf.episode_duration, 0)), 0)
  ) || 0

  useEffect(() => {
    fetchAndSetEpisodesData(language, audio_guide_list, setEpisodesAll)
  }, [])

  useEffect(() => {
    if (episodesAll && !!episodesAll.length) {
      fetchEpisodesArtworkData(episodesAll, setEpisodesArtworkData)
    } else {
      setEpisodesArtworkData([])
    }
  }, [episodesAll])

  if (!episodesAll || !episodesArtworkData) {
    return <DonutSpinner colors={colors} />
  }

  const handleOpenMap = (artwork) => {
    setShowMap(true)
    setMapArtwork(artwork)
  }

  const handleCloseMap = () => {
    setMapClosing(true)
    setTimeout(() => {
      setShowMap(false)
      setMapClosing(false)
    }, 400)
    
  }
    
  return (
    <Fragment>
    <div style={{display: showMap ? 'block' : 'none'}}>
      <AudioGuideMapModal 
        show={showMap} 
        handleClose={handleCloseMap} 
        mapClosing={mapClosing}
        artwork={mapArtwork}
      >
          <p>Modal</p>
      </AudioGuideMapModal>
    </div>
    <section className={styles.AudioGuideEpisodesList}>
      <div className={`container-fluid`}>
        <div className="row">
          <div className='col-xs-12'>
            <div className={styles.duration}>
              {episodesFiltered && `${episodesFiltered.length} ${LIB.audio_guide_episodes}`}
              {listGuideTotalDuration !== 0 && (
                <Fragment>
                  <span>&nbsp;&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;&nbsp;</span>
                  {`${secondsToMinutes(listGuideTotalDuration)} min ${LIB.audio_guide_total.toLowerCase()} `}
                </Fragment>
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className={`col-xs-12 ${styles.introText}`} dangerouslySetInnerHTML={{ __html: audio_guide_list_description }} />
        </div>
        <div className="row">
          {guide.link_help_page_url && (
            <Link to={getPathFromAbsoluteUrl(guide.link_help_page_url)} className={`${styles.help}`}>
              <div>
                {guide.link_help_page_text}
              </div>
              <div className={styles.helpIcon}>
                <div>?</div>
              </div>
            </Link>
          )}
        </div>
        {episodesFiltered.map((audioEpisode, idx) => {
          const artwork = episodesArtworkData.find(artwork => artwork.object_number === _try(() => audioEpisode.acf.artwork_id))
          return (
            <AudioGuideEpisode
              language={language}
              audioGuideName={title}
              audioEpisode={audioEpisode}
              artwork={artwork}
              colors={colors}
              key={`age-${idx}`}
              handleOpenMap={handleOpenMap}
              handleCloseMap={handleCloseMap}
            />
          )
        })}
      </div>

    </section>
    </Fragment>
  )
  
}

export default withRouter(AudioGuideEpisodesList)



// WEBPACK FOOTER //
// ./src/components/AudioGuideComponents/AudioGuideEpisodesList/AudioGuideEpisodesList.js